import { Logger } from '../../../utils/logger';
import { BRAND_PURPLE, COLOR_GOOD, COLOR_WARN, COLOR_POOR, TEXT_DISABLED } from '../../../utils/colors';

const logger = new Logger('ConciergeInsightsParser');

/**
 * Concierge Insights Parser
 * Transforms Claude's structured JSON analysis into elegant data for display
 * Updated to handle the new JSON format instead of markdown
 */

export const parseConciergeInsights = (rawAnalysis) => {
  if (!rawAnalysis) return null;

  logger.debug('🔍 Parser: Received rawAnalysis:', rawAnalysis.substring(0, 200) + '...');

  // Try to parse as JSON first (new format)
  const jsonInsights = parseJsonInsights(rawAnalysis);
  if (jsonInsights) {
    logger.debug('✅ Parser: Successfully parsed JSON format');
    return jsonInsights;
  }

  // Fallback to markdown parsing (legacy format)
  logger.debug('⚠️ Parser: JSON parsing failed, trying markdown fallback');
  return parseMarkdownInsights(rawAnalysis);
};

/**
 * Parse the new JSON format from Claude
 */
const parseJsonInsights = (rawAnalysis) => {
  try {
    // Extract JSON from the raw analysis (might be wrapped in ```json blocks)
    let jsonText = rawAnalysis;
    
    // Remove ```json and ``` wrappers if present
    const jsonMatch = rawAnalysis.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      jsonText = jsonMatch[1];
    }


    // Parse the JSON
    const parsedData = JSON.parse(jsonText);
    logger.debug('🎯 Parser: Parsed JSON data:', parsedData);

    if (!parsedData.summary || !parsedData.detailed_sections) {
      logger.debug('❌ Parser: JSON missing required structure');
      return null;
    }

    const insights = {
      summary: parseJsonSummary(parsedData.summary),
      detailedSections: parseJsonDetailedSections(parsedData.detailed_sections),
      scorecard: parsedData.scorecard || [],
      tradeoffs: parsedData.key_tradeoffs || [],
      flags: parsedData.flags || {},
      mitigationActions: parsedData.mitigation_actions || [],
      metadata: {
        format: 'json',
        timestamp: new Date().toISOString(),
        sections_count: parsedData.detailed_sections?.length || 0
      }
    };

    logger.debug('✅ Parser: Final insights structure:', insights);
    return insights;

  } catch (error) {
    logger.debug('❌ Parser: JSON parsing failed:', error.message);
    return null;
  }
};

/**
 * Parse the summary section from JSON
 */
const parseJsonSummary = (summaryData) => {
  return {
    strengths: summaryData.key_strengths || [],
    risks: summaryData.primary_risks || [],
    recommendations: summaryData.top_recommendations || [],
    assessment: summaryData.overall_assessment || ''
  };
};

/**
 * Parse the detailed sections from JSON
 */
const parseJsonDetailedSections = (sectionsData) => {
  if (!Array.isArray(sectionsData)) {
    logger.debug('❌ Parser: detailed_sections is not an array');
    return [];
  }

  return sectionsData.map((section, index) => {
    // Enhanced content processing for better formatting
    const processedContent = section.content ? 
      formatContentForDisplay(section.content) : '';
    
    // Extract key points from content if not explicitly provided
    const keyPoints = section.key_points || 
                     section.keyPoints || 
                     extractKeyPointsFromContent(section.content) || 
                     [];

    return {
      id: `section-${index}`,
      title: section.title || `Analysis Section ${index + 1}`,
      content: processedContent,
      strengths: section.strengths || [],
      concerns: section.concerns || section.gaps || [],
      recommendations: section.recommendations || [],
      keyPoints: keyPoints,
      priority: section.priority || 'medium',
      category: section.category || getSectionType(section.title || '')
    };
  });
};

/**
 * Format content for better display
 */
const formatContentForDisplay = (content) => {
  if (!content) return '';
  
  // Clean up excessive whitespace
  let formatted = content.replace(/\s+/g, ' ').trim();
  
  // Ensure proper sentence endings
  if (formatted && !formatted.endsWith('.') && !formatted.endsWith('!') && !formatted.endsWith('?')) {
    formatted += '.';
  }
  
  // Break very long paragraphs into shorter sentences for readability
  if (formatted.length > 300) {
    const sentences = formatted.split(/(?<=[.!?])\s+/);
    if (sentences.length > 1) {
      // Group sentences into smaller paragraphs
      const paragraphs = [];
      let currentParagraph = '';
      
      sentences.forEach(sentence => {
        if (currentParagraph.length + sentence.length > 200 && currentParagraph) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = sentence;
        } else {
          currentParagraph += (currentParagraph ? ' ' : '') + sentence;
        }
      });
      
      if (currentParagraph) {
        paragraphs.push(currentParagraph.trim());
      }
      
      return paragraphs.join('\n\n');
    }
  }
  
  return formatted;
};

/**
 * Extract key points from content text
 */
const extractKeyPointsFromContent = (content) => {
  if (!content) return [];
  
  const keyPoints = [];
  
  // Look for numbered lists (1. 2. 3. etc.)
  const numberedMatches = content.match(/\d+\.\s+[^.]+[.!?]/g);
  if (numberedMatches) {
    numberedMatches.forEach(match => {
      const cleanPoint = match.replace(/^\d+\.\s*/, '').trim();
      if (cleanPoint && cleanPoint.length > 10) {
        keyPoints.push(cleanPoint);
      }
    });
  }
  
  // Look for bullet points
  const bulletMatches = content.match(/[•-]\s+[^.]+[.!?]/g);
  if (bulletMatches) {
    bulletMatches.forEach(match => {
      const cleanPoint = match.replace(/^[•-]\s*/, '').trim();
      if (cleanPoint && cleanPoint.length > 10) {
        keyPoints.push(cleanPoint);
      }
    });
  }
  
  // If no structured points found, try to extract key sentences
  if (keyPoints.length === 0) {
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 20);
    if (sentences.length > 1) {
      // Take the first few meaningful sentences as key points
      keyPoints.push(...sentences.slice(0, 3).map(s => s.trim() + '.'));
    }
  }
  
  return keyPoints.slice(0, 5); // Limit to 5 key points max
};

/**
 * Legacy markdown parser (fallback)
 */
const parseMarkdownInsights = (rawAnalysis) => {
  const insights = {
    summary: null,
    detailedSections: []
  };

  // Split into summary and detailed sections
  const summaryMatch = rawAnalysis.match(/## SUMMARY([\s\S]*?)(?=## DETAILED|$)/i);
  const detailedMatch = rawAnalysis.match(/## DETAILED ANALYSIS([\s\S]*)/i);

  // Parse Summary Section
  if (summaryMatch) {
    insights.summary = parseSummarySection(summaryMatch[1]);
  }

  // Parse Detailed Sections
  if (detailedMatch) {
    insights.detailedSections = parseDetailedSections(detailedMatch[1]);
  }

  return insights;
};

const parseSummarySection = (summaryText) => {
  const summary = {
    strengths: [],
    risks: [],
    recommendations: [],
    assessment: ''
  };

  // Extract Key Team Strengths
  const strengthsMatch = summaryText.match(/Key Team Strengths?:([\s\S]*?)(?=Primary Risks?:|Top Recommendations?:|Overall Assessment:|$)/i);
  if (strengthsMatch) {
    summary.strengths = extractBulletPoints(strengthsMatch[1]);
  }

  // Extract Primary Risks
  const risksMatch = summaryText.match(/Primary Risks?:([\s\S]*?)(?=Top Recommendations?:|Overall Assessment:|$)/i);
  if (risksMatch) {
    summary.risks = extractBulletPoints(risksMatch[1]);
  }

  // Extract Top Recommendations
  const recommendationsMatch = summaryText.match(/Top Recommendations?:([\s\S]*?)(?=Overall Assessment:|$)/i);
  if (recommendationsMatch) {
    summary.recommendations = extractNumberedPoints(recommendationsMatch[1]);
  }

  // Extract Overall Assessment
  const assessmentMatch = summaryText.match(/Overall Assessment:([\s\S]*?)$/i);
  if (assessmentMatch) {
    summary.assessment = assessmentMatch[1].trim();
  }

  return summary;
};

const parseDetailedSections = (detailedText) => {
  const sections = [];
  
  // Split by ### headers (detailed sections)
  const sectionMatches = detailedText.split(/###\s*\d+\.\s*/);
  
  sectionMatches.forEach((sectionText, index) => {
    if (index === 0 || !sectionText.trim()) return; // Skip empty first section
    
    const section = parseDetailedSection(sectionText);
    if (section) {
      sections.push(section);
    }
  });

  return sections;
};

const parseDetailedSection = (sectionText) => {
  const lines = sectionText.split('\n').filter(line => line.trim());
  if (lines.length === 0) return null;

  const section = {
    title: '',
    content: '',
    strengths: [],
    concerns: [],
    recommendations: [],
    keyPoints: [] // New field for structured content
  };

  // First line is usually the title
  section.title = lines[0].trim();

  let currentCategory = 'content';
  let contentLines = [];

  lines.slice(1).forEach(line => {
    const trimmedLine = line.trim();
    
    if (trimmedLine.toLowerCase().includes('strength')) {
      currentCategory = 'strengths';
    } else if (trimmedLine.toLowerCase().includes('concern') || trimmedLine.toLowerCase().includes('gap')) {
      currentCategory = 'concerns';
    } else if (trimmedLine.toLowerCase().includes('recommendation')) {
      currentCategory = 'recommendations';
    } else if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
      // Bullet point
      const cleanPoint = trimmedLine.replace(/^[•-]\s*/, '');
      if (currentCategory === 'strengths') {
        section.strengths.push(cleanPoint);
      } else if (currentCategory === 'concerns') {
        section.concerns.push(cleanPoint);
      } else if (currentCategory === 'recommendations') {
        section.recommendations.push(cleanPoint);
      } else {
        // General bullet points become key points
        section.keyPoints.push(cleanPoint);
      }
    } else if (trimmedLine && !trimmedLine.includes(':')) {
      // Check for numbered lists (1. 2. 3. etc.) and convert to key points
      const numberedListMatch = trimmedLine.match(/(\d+\.\s+[^0-9]+?)(?=\s+\d+\.|$)/g);
      if (numberedListMatch) {
        // This line contains numbered items, extract them as key points
        numberedListMatch.forEach(item => {
          const cleanItem = item.replace(/^\d+\.\s*/, '').trim();
          if (cleanItem) {
            section.keyPoints.push(cleanItem);
          }
        });
      } else {
        // Regular content line - break into sentences for better readability
        if (trimmedLine.length > 100) {
          // Check if this might be a run-on sentence with numbered items
          const hasNumbers = /\d+\.\s+/.test(trimmedLine);
          if (hasNumbers) {
            // Split by numbered items
            const parts = trimmedLine.split(/(?=\d+\.\s+)/).filter(part => part.trim());
            parts.forEach(part => {
              const cleanPart = part.replace(/^\d+\.\s*/, '').trim();
              if (cleanPart) {
                section.keyPoints.push(cleanPart);
              }
            });
          } else {
            // Split long sentences at periods for better formatting
            const sentences = trimmedLine.split('. ').filter(s => s.trim());
            sentences.forEach((sentence, index) => {
              const cleanSentence = sentence.trim();
              if (cleanSentence) {
                if (index === sentences.length - 1 && !cleanSentence.endsWith('.')) {
                  contentLines.push(cleanSentence + '.');
                } else if (!cleanSentence.endsWith('.')) {
                  contentLines.push(cleanSentence + '.');
                } else {
                  contentLines.push(cleanSentence);
                }
              }
            });
          }
        } else {
          contentLines.push(trimmedLine);
        }
      }
    }
  });

  section.content = contentLines.join(' ');
  return section;
};

const extractBulletPoints = (text) => {
  const points = [];
  const lines = text.split('\n');
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
      const cleanPoint = trimmedLine.replace(/^[•-]\s*/, '');
      if (cleanPoint) {
        points.push(cleanPoint);
      }
    }
  });
  
  return points;
};

const extractNumberedPoints = (text) => {
  const points = [];
  const lines = text.split('\n');
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    const numberedMatch = trimmedLine.match(/^\d+\.\s*(.+)/);
    if (numberedMatch) {
      points.push(numberedMatch[1]);
    }
  });
  
  return points;
};

// Helper function to determine section type for styling
export const getSectionType = (title) => {
  const titleLower = title.toLowerCase();
  
  if (titleLower.includes('technical') || titleLower.includes('capability')) {
    return 'technical';
  } else if (titleLower.includes('team') || titleLower.includes('chemistry') || titleLower.includes('dynamic')) {
    return 'team';
  } else if (titleLower.includes('performance') || titleLower.includes('prediction')) {
    return 'performance';
  } else if (titleLower.includes('risk') || titleLower.includes('analysis')) {
    return 'risk';
  } else if (titleLower.includes('optimization') || titleLower.includes('recommendation')) {
    return 'optimization';
  }
  
  return 'general';
};

// Helper function to get appropriate icon for section type
export const getSectionIcon = (sectionType) => {
  const iconMap = {
    technical: 'Engineering',
    team: 'Groups',
    performance: 'TrendingUp',
    risk: 'Warning',
    optimization: 'Tune',
    general: 'Analytics'
  };
  
  return iconMap[sectionType] || 'Analytics';
};

// Helper function to get color scheme for section type
export const getSectionColors = (sectionType) => {
  const colorMap = {
    technical:    { primary: BRAND_PURPLE, light: `${BRAND_PURPLE}1A`, border: `${BRAND_PURPLE}33` },
    team:         { primary: COLOR_GOOD,   light: `${COLOR_GOOD}1A`,   border: `${COLOR_GOOD}33`   },
    performance:  { primary: COLOR_WARN,   light: `${COLOR_WARN}1A`,   border: `${COLOR_WARN}33`   },
    risk:         { primary: COLOR_POOR,   light: `${COLOR_POOR}1A`,   border: `${COLOR_POOR}33`   },
    optimization: { primary: BRAND_PURPLE, light: `${BRAND_PURPLE}1A`, border: `${BRAND_PURPLE}33` },
    general:      { primary: TEXT_DISABLED, light: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  };

  return colorMap[sectionType] || colorMap.general;
};
